import type { ChatOverview, ChatSummary, ChatUser, GroupChat } from '@volt/shared';

export interface ChatListEntry {
  key: string;
  kind: 'direct' | 'group';
  id: string;
  title: string;
  photoUrl: string | null;
  initials: string;
  preview: string;
  previewMine: boolean;
  lastAt: string | null;
  unread: number;
  pinnedAt: string | null;
  archived: boolean;
  muted: boolean;
  blocked: boolean;
  alias: string | null;
  receipt: '' | '✓' | '✓✓';
}

export function friendLabel(friend: ChatUser): string {
  return friend.firstName || (friend.username ? `@${friend.username}` : `ID ${friend.publicId}`);
}

export function initialsOf(name: string): string {
  return (
    name
      .split(/[\s_]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => Array.from(part)[0] ?? '')
      .join('')
      .toUpperCase() || '?'
  );
}

export function aliasMap(overview: ChatOverview | null): Map<string, string> {
  const map = new Map<string, string>();
  for (const pref of overview?.prefs ?? []) {
    if (pref.alias)
      map.set(`${pref.kind === 'direct' ? 'friend' : 'group'}:${pref.targetId}`, pref.alias);
  }
  return map;
}

function receiptFor(summary: ChatSummary | undefined, userId: string, friendId?: string) {
  const last = summary?.lastMessage;
  if (!last || last.senderId !== userId || !friendId) return '';
  const receipt = last.receipts?.find((entry) => entry.userId === friendId);
  if (receipt?.readAt) return '✓✓';
  if (receipt?.deliveredAt) return '✓';
  return '';
}

export function buildChatList(input: {
  friends: ChatUser[];
  groups: GroupChat[];
  overview: ChatOverview | null;
  userId: string;
}): ChatListEntry[] {
  const { friends, groups, overview, userId } = input;
  const aliases = aliasMap(overview);
  const summaries = new Map<string, ChatSummary>();
  for (const summary of overview?.summaries ?? []) {
    summaries.set(`${summary.kind}:${summary.targetId}`, summary);
  }
  const prefs = new Map<string, NonNullable<ChatOverview['prefs'][number]>>();
  for (const pref of overview?.prefs ?? []) prefs.set(`${pref.kind}:${pref.targetId}`, pref);
  const blocked = new Set(overview?.blockedIds ?? []);

  const entry = (
    kind: 'direct' | 'group',
    id: string,
    title: string,
    photoUrl: string | null,
    friendId?: string,
  ): ChatListEntry => {
    const summary = summaries.get(`${kind}:${id}`);
    const pref = prefs.get(`${kind}:${id}`);
    const last = summary?.lastMessage ?? null;
    return {
      key: `${kind === 'direct' ? 'friend' : 'group'}:${id}`,
      kind,
      id,
      title,
      photoUrl,
      initials: initialsOf(title),
      preview: last?.content ?? '',
      previewMine: !!last && last.senderId === userId,
      lastAt: last?.createdAt ?? null,
      unread: summary?.unreadCount ?? 0,
      pinnedAt: pref?.pinnedAt ?? null,
      archived: !!pref?.archivedAt,
      muted: !!pref?.mutedAt,
      blocked: kind === 'direct' && blocked.has(id),
      alias: pref?.alias ?? null,
      receipt: receiptFor(summary, userId, friendId),
    };
  };

  return [
    ...friends.map((friend) =>
      entry(
        'direct',
        friend.id,
        aliases.get(`friend:${friend.id}`) ?? friendLabel(friend),
        friend.photoUrl,
        friend.id,
      ),
    ),
    ...groups.map((group) =>
      entry('group', group.id, aliases.get(`group:${group.id}`) ?? group.title, null),
    ),
  ];
}

// Direct chats without visible messages live only in the friends strip; groups always stay listed.
export function visibleChatList(
  entries: ChatListEntry[],
  options: { archived: boolean },
): ChatListEntry[] {
  return entries
    .filter(
      (item) =>
        (item.kind === 'group' || item.lastAt !== null) && item.archived === options.archived,
    )
    .sort(
      (a, b) =>
        Number(!!b.pinnedAt) - Number(!!a.pinnedAt) ||
        (a.pinnedAt && b.pinnedAt ? b.pinnedAt.localeCompare(a.pinnedAt) : 0) ||
        (b.lastAt ?? '').localeCompare(a.lastAt ?? '') ||
        a.title.localeCompare(b.title),
    );
}

export function friendStrip(entries: ChatListEntry[]): ChatListEntry[] {
  return entries
    .filter((item) => item.kind === 'direct')
    .sort(
      (a, b) => (b.lastAt ?? '').localeCompare(a.lastAt ?? '') || a.title.localeCompare(b.title),
    );
}

export function formatListTime(iso: string, now: Date, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  if (date.toDateString() === now.toDateString())
    return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(date);
  const days = (now.getTime() - date.getTime()) / 86_400_000;
  return new Intl.DateTimeFormat(
    locale,
    days < 7 ? { weekday: 'short' } : { day: '2-digit', month: '2-digit' },
  ).format(date);
}
